import type { QueryClient } from '@tanstack/react-query';
import type { i18n as I18nInstance, ResourceLanguage } from 'i18next';
import type { ComponentType, ReactNode } from 'react';
import type { RouteObject } from 'react-router';

import type { FileRef, Language } from '../api/types';
import type { RealtimeManager } from '../realtime/manager';

/** Icon component (lucide-react icons satisfy this). */
export type IconComponent = ComponentType<{
  className?: string;
  strokeWidth?: number;
  'aria-hidden'?: boolean;
}>;

/**
 * Sidebar sections, top to bottom: `primary` (quick entries: notifications, search), `main`
 * (Feed … Knowledge base), `admin` (visible to administrators only).
 */
export type NavSection = 'primary' | 'main' | 'admin';

export interface NavItem {
  id: string;
  /** Namespaced i18n key, e.g. `tasks:nav.title`. */
  labelKey: string;
  icon: IconComponent;
  /** Route path; active when the current path starts with it (`end: true` → exact match). */
  to: string;
  end?: boolean;
  /** Sort order inside the section (lower first). */
  order: number;
  section?: NavSection;
  /** Hide for non-admin users (implied for the `admin` section). */
  adminOnly?: boolean;
  /**
   * Shown to people who hold at least one of these permissions (administrators hold them all), instead of to administrators
   * only: an item of the `admin` section that a delegated role can reach (the administration area opens for the permissions of
   * its tabs). Takes the place of `adminOnly`.
   */
  permissions?: readonly string[];
  /**
   * Hook returning a badge counter (e.g. unread messages). Must be a stable function defined at module
   * scope; it runs inside the sidebar for as long as the item is rendered.
   */
  useCounter?: () => number | undefined;
  /**
   * Hook deciding whether the item is shown to the signed-in user (e.g. only to the operators of open lines). Must be a
   * stable function defined at module scope; the sidebar hides the item while it returns false.
   */
  useVisible?: () => boolean;
}

/**
 * A section of the messenger's left pane next to the chats (e.g. open lines): the messenger shows the tabs of all
 * modules above its list as soon as more than one is visible. Each tab is its own route (`to`).
 */
export interface MessengerTab {
  id: string;
  /** Namespaced i18n key, e.g. `openlines:messengerTab`. */
  labelKey: string;
  icon: IconComponent;
  /** Route of the section (active when the current path starts with it; the chats tab matches exactly). */
  to: string;
  order: number;
  /** Badge counter hook (stable, module scope). */
  useCounter?: () => number | undefined;
  /** Visibility hook (stable, module scope); hidden while it returns false. */
  useVisible?: () => boolean;
}

export interface GroupTab {
  id: string;
  labelKey: string;
  order: number;
  element: (groupId: string) => ReactNode;
}

export interface ProfileTab {
  id: string;
  labelKey: string;
  order: number;
  element: (userId: string) => ReactNode;
  /**
   * Whether the tab is offered on this profile (default: always). `self`: the profile is the signed-in user's own;
   * `isAdmin`: the signed-in user is a portal administrator.
   */
  visible?: (context: { userId: string; self: boolean; isAdmin: boolean }) => boolean;
}

/**
 * A section another module adds to a host page's own navigation (e.g. the CRM's section strip gets "Web forms" from the
 * `webforms` module). The host shows it at `/<page>/<id>` (and below), renders `element` in its content area and keeps
 * its own header; the section reads the rest of the URL itself.
 */
export interface PageSection {
  /** The host page: `crm`, `contact-center`. */
  page: string;
  /** The URL segment after the page (`forms` → `/crm/forms`). */
  id: string;
  labelKey: string;
  icon: IconComponent;
  order: number;
  /** Offered to administrators only (the host decides who they are). */
  adminOnly?: boolean;
  /** Used now and then: the host lists it in its tools menu and shows it in its strip only while it is open. */
  tool?: boolean;
  /**
   * Hook returning a badge counter for the section's tab (e.g. the overdue goals on the goals tab). Must be a stable function
   * defined at module scope; it runs inside the host's strip for as long as the tab is rendered.
   */
  useCounter?: () => number | undefined;
  /**
   * Hook returning where the section's tab leads while the section is not open (default: the section's own address), for a
   * section that remembers where the person was (the goals tab goes back to the map they had open). Must be a stable function
   * defined at module scope; `undefined` means the default.
   */
  useTarget?: () => string | undefined;
  element: () => ReactNode;
}

/** A document processes work with: its type (`crm.deal`, `tasks.task`, `lists.item:<listId>`) and id. */
export interface DocumentRef {
  type: string;
  id: string;
}

/**
 * A tab another module adds to the card of a document (a CRM item, a task, an item of a list), e.g. the "Processes" tab
 * of the `bizproc` module. `documentTypes` are patterns as in the backend's `DocumentTypes.Matches`: `*`, `module.*`,
 * `module.type:*` or an exact type. The host renders the tabs of `registry.documentTabs(type)` after its own.
 */
export interface DocumentTab {
  id: string;
  /** Namespaced i18n key of the tab's label, e.g. `bizproc:documentTab.title`. */
  labelKey: string;
  order: number;
  documentTypes: readonly string[];
  /** The tab comes right after the card's general tab, before the card's own tabs (default: after them). */
  afterGeneral?: boolean;
  element: (document: DocumentRef) => ReactNode;
}

/**
 * Tabs a module takes off the card of a kind of document: `tabs` are the ids of the card's own tabs (`products`, `related`,
 * `timeline`, `activities`) and of the tabs other modules add through {@link DocumentTab}. Only the tab is not offered; the
 * data and the API behind it stay (somebody may still fill them in another way). The first thing it is for: a build whose
 * deals have no product rows takes the «Products» tab off the deal's card.
 */
export interface HiddenDocumentTabs {
  /** Patterns as for {@link DocumentTab.documentTypes}. */
  documentTypes: readonly string[];
  tabs: readonly string[];
}

/**
 * An action another module adds to the bulk bar of a list of documents (the «Run process» button of the `bizproc` module on
 * the list of a user-defined CRM type): the host renders `element` next to its own actions for the selected documents of
 * `documentTypes` (patterns as for `DocumentTab`); `onDone` clears the selection once the action is finished.
 */
export interface DocumentBulkAction {
  id: string;
  order: number;
  documentTypes: readonly string[];
  element: (context: { type: string; ids: readonly string[]; onDone: () => void }) => ReactNode;
}

/**
 * A tab another module adds to the settings of a document type (a list's settings: "Business processes"). The host
 * renders the tabs of `registry.documentSettingsTabs(type)` after its own; `type` is the document type of the
 * thing being configured (`lists.item:<listId>`).
 */
export interface DocumentSettingsTab {
  id: string;
  labelKey: string;
  order: number;
  documentTypes: readonly string[];
  element: (context: { type: string }) => ReactNode;
}

/**
 * A tab another module adds to the feed's composer next to the post types (message, poll, gratitude, important): the
 * «Process» tab of the `lists` module lists the processes an employee starts from the feed. The composer shows
 * `registry.feedComposerTabs` after its own types, in `order`, and renders `element` when the tab is chosen; `onDone` closes
 * the composer once the tab has done its work (the request was submitted).
 */
export interface FeedComposerTab {
  id: string;
  /** Namespaced i18n key of the tab's label, e.g. `lists:composer.tab`. */
  labelKey: string;
  icon: IconComponent;
  order: number;
  /** `groupId` is set when the composer belongs to a group's page (the tab may hide itself or ignore it). */
  element: (context: { groupId?: string; onDone: () => void }) => ReactNode;
}

/** A global search hit (search module wire format). */
export interface SearchResult {
  module: string;
  entityType: string;
  entityId: string;
  title: string;
  snippet?: string | null;
  url: string;
  updatedAt?: string | null;
  /** Who wrote it (messages, comments, posts, tasks…); null/absent when the document has no author. */
  authorId?: string | null;
  meta?: Record<string, string> | null;
}

/** A hit's title and snippet with the matches marked (styled `<mark>` runs), built by the search page. */
export interface SearchResultHighlights {
  title: ReactNode;
  snippet: ReactNode | null;
}

/**
 * Renders a module's own hits on the search page. The page wraps it in the hit's link (the row with hover, focus and
 * `data-testid="search-result"`), so a renderer draws content only — no links, buttons or other interactive elements.
 * `result.title`/`snippet` are plain text; `highlights` carries the same texts with the matches marked.
 */
export type SearchResultRenderer = ComponentType<{
  result: SearchResult;
  highlights?: SearchResultHighlights;
}>;

/** In-app notification (notifications module wire format; mirrors NotificationRequested, ARCHITECTURE §4.7). */
export interface NotificationItem {
  id: string;
  module: string;
  type: string;
  actor?: { id: string; fullName: string; avatarUrl: string | null } | null;
  entityId?: string | null;
  link?: string | null;
  params: Record<string, string>;
  fallbackText: string;
  isRead: boolean;
  createdAt: string;
}

export type NotificationRenderer = ComponentType<{ notification: NotificationItem }>;

export interface TopBarWidget {
  id: string;
  order: number;
  component: ComponentType;
}

/**
 * A URL-addressable side panel ("slider") type. Opened with `useSidePanel().open(type, id)`;
 * the shell renders `component` with the entity id inside the stacked panel chrome.
 */
export interface SidePanelDefinition {
  type: string;
  component: ComponentType<{ id: string }>;
  /** Preferred width (default `lg`; `full` takes all the screen the panel's close tab leaves). */
  size?: 'md' | 'lg' | 'xl' | 'full';
  /** Namespaced i18n key used as the accessible dialog name (e.g. `tasks:panel.title`). */
  titleKey?: string;
  /**
   * Whether the panel is modal on the page at `pathname` (default: always). A non-modal panel leaves the page usable
   * beside it — no dimming, outside clicks reach the page and do not close it, it takes the focus only when asked —
   * and opening another entity of the same type swaps the panel's content in place (a canvas and its item cards).
   */
  modal?: (pathname: string) => boolean;
  /**
   * Whether the panel is offered at all (default: always): a hook (stable, defined at module scope) that says no while the thing the
   * panel shows is switched off — the CRM's lead card while «use leads» is off. Other modules ask `useSidePanelAvailable(type)` before
   * they offer to create or open such a record.
   */
  useAvailable?: () => boolean;
}

/** A group of entries contributed to the ⌘K command palette. */
export interface CommandSource {
  id: string;
  /** Namespaced i18n key for the group heading. */
  headingKey: string;
  order: number;
  /** Minimum query length before `useItems` is asked for results (default 0). */
  minQueryLength?: number;
  /**
   * List the group right after the matching navigation pages, before people and the other sources (the search
   * module's "Search everywhere for …"): Enter after typing a query then runs it, and results that arrive later
   * do not move the selection.
   */
  first?: boolean;
  /** Hook returning items for the current query; runs only while the palette is open. */
  useItems: (query: string) => { items: CommandItem[]; isLoading?: boolean };
  /**
   * A row shown between the palette's input and its list while the query is long enough for this source (the search
   * module's filters: section, period, "only mine"). It is a plain component (its buttons are tab stops of the dialog);
   * keep the state it edits outside it and reset it in `onClose`.
   */
  toolbar?: ComponentType;
  /** Called whenever the palette closes (however it was closed), e.g. to reset what `toolbar` edited. */
  onClose?: () => void;
}

export interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon?: IconComponent;
  /** Custom leading visual (e.g. an avatar); wins over `icon`. */
  leading?: ReactNode;
  keywords?: string[];
  /** Navigate to this path when chosen. */
  to?: string;
  /** Or run this action. */
  perform?: () => void;
}

/** What a picker of an attachment source gets from the uploader that opened it. */
export interface AttachmentSourcePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Hands the chosen files (stored through the Files API, owned by the current user) to the uploader. */
  onPick: (files: FileRef[]) => void;
  multiple: boolean;
  /** The uploader's `accept` list (`image/*,.pdf`); only matching files may be chosen. */
  accept?: string;
  /** How many more files the uploader takes (undefined = no limit). */
  maxFiles?: number;
}

/**
 * Another place attachments can come from besides the computer (e.g. "Choose from Disk"): `FileUploader` and the
 * composers' attach button offer every registered source next to the upload button.
 */
export interface AttachmentSource {
  id: string;
  /** Namespaced i18n key of the button label, e.g. `disk:attach.fromDisk`. */
  labelKey: string;
  icon: IconComponent;
  order: number;
  /** The picker dialog (rendered only while open). */
  picker: ComponentType<AttachmentSourcePickerProps>;
}

export interface ModuleSetupContext {
  queryClient: QueryClient;
  realtime: RealtimeManager;
  i18n: I18nInstance;
}

export type ModuleLocales = Record<Language, ResourceLanguage>;

/** The contract every frontend module exports from `src/modules/<id>/index.ts` as `module` (ARCHITECTURE §6.2). */
/**
 * The face of the sign-in pages (login, invitation, password recovery) a module may give the product — an industry edition's photo and
 * name instead of the plain Orbita pages. At most one module may; the pages are the plain ones without it.
 */
export interface AuthBranding {
  /** URL of the photograph behind the pages (the module imports its asset, so it ships only in builds that include the module). */
  backgroundUrl: string;
  /** The product's name as the pages show it, in place of the Orbita logo. */
  wordmark: ReactNode;
  /** Namespaced i18n key of the line under the wordmark (`agro:brand.tagline`). */
  taglineKey?: string;
  /** The accent of the pages (a CSS color): the button, the links and the focus rings. */
  accent?: string;
}

export interface OrbitaModule {
  /** Module id (kebab case); also its i18n namespace and query-key root. */
  id: string;
  /** Routes mounted inside the authenticated shell. */
  routes: RouteObject[];
  /** Routes outside the shell that need no sign-in (e.g. a public link page); keep them inside the module's URL space. */
  publicRoutes?: RouteObject[];
  nav?: NavItem[];
  groupTabs?: GroupTab[];
  profileTabs?: ProfileTab[];
  /** Sections of other modules' pages (the CRM's and the contact center's sections). */
  pageSections?: PageSection[];
  searchRenderers?: Record<string, SearchResultRenderer>;
  notificationRenderers?: Record<string, NotificationRenderer>;
  topBarWidgets?: TopBarWidget[];
  sidePanels?: SidePanelDefinition[];
  /** The face of the sign-in pages (see {@link AuthBranding}). */
  authBranding?: AuthBranding;
  /**
   * Widths for side panel types another module registered, by type (`{ company: 'full' }`): the module that owns the panel keeps
   * its own `size` for everybody else, and a build that includes this module opens the panel wider. Two modules may not size the
   * same type.
   */
  sidePanelSizes?: Record<string, NonNullable<SidePanelDefinition['size']>>;
  commands?: CommandSource[];
  /** Extra sources of attachments offered by `FileUploader` (e.g. "Choose from Disk"). */
  attachmentSources?: AttachmentSource[];
  /** Sections of the messenger's left pane (the chats are the messenger's own tab). */
  messengerTabs?: MessengerTab[];
  /** Tabs of the card of a document (CRM items, tasks, items of lists). */
  documentTabs?: DocumentTab[];
  /** Tabs this module takes off the cards of some document types (see {@link HiddenDocumentTabs}). */
  hiddenDocumentTabs?: HiddenDocumentTabs[];
  /** Tabs of the settings of a document type (a list's settings). */
  documentSettingsTabs?: DocumentSettingsTab[];
  /** Actions of the bulk bar of a list of documents (the CRM list of a user-defined type). */
  documentBulkActions?: DocumentBulkAction[];
  /** Tabs of the feed's composer next to the post types (the `lists` module's «Process»). */
  feedComposerTabs?: FeedComposerTab[];
  /**
   * Translations bundled with the definition (namespace = module id). Optional: a module's texts are its
   * `locales/{ru,en,uk}.json` files, which the runtime loads per language outside the main bundle (ARCHITECTURE §6.5);
   * eager resources are for definitions outside the modules folder (tests).
   */
  locales?: ModuleLocales;
  /** Called once at startup (register realtime handlers, prefetch, etc.). */
  setup?: (ctx: ModuleSetupContext) => void;
}

/** Identity helper giving module definitions full type inference. */
export const defineModule = (module: OrbitaModule): OrbitaModule => module;
